"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metrics = [{
  index: "01/04",
  value: 42,
  suffix: "%",
  label: "AVG REVENUE UPLIFT",
  sparkline: "M0,30 L15,28 L30,25 L45,22 L60,18 L75,14 L90,9 L105,5 L120,2"
}, {
  index: "02/04",
  value: 28,
  suffix: "%",
  label: "OTA COST REDUCTION",
  sparkline: "M0,32 L15,30 L30,26 L45,24 L60,19 L75,15 L90,11 L105,7 L120,3"
}, {
  index: "03/04",
  value: 150,
  suffix: "+",
  label: "HOTELS OPTIMIZED",
  sparkline: "M0,28 L15,26 L30,23 L45,19 L60,16 L75,12 L90,10 L105,6 L120,3"
}, {
  index: "04/04",
  value: 95,
  suffix: "%",
  label: "DIRECT BOOKING GROWTH",
  sparkline: "M0,30 L15,27 L30,24 L45,21 L60,17 L75,13 L90,10 L105,7 L120,4"
}];
const tickerItems = ["REVENUE-AUDIT / 03.18.25", "OTA-STRATEGY / 03.22.25", "TECH-STACK / 04.02.25", "RATE-PARITY / 04.11.25", "DIRECT-BOOK / 04.19.25", "MARKET-SHARE / 05.03.25", "DYNAMIC-PRICING / 05.14.25", "FORECASTING / 05.27.25", "DIGITAL-GROWTH / 06.08.25", "PROFIT-MARGIN / 06.21.25"];
function AnimatedCounter({
  value,
  suffix,
  isActive
}: {
  value: number;
  suffix: string;
  isActive: boolean;
}) {
  const [count, setCount] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isActive || startedRef.current) return;
    startedRef.current = true;
    const duration = 1400;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * value));
      if (progress < 1) requestAnimationFrame(tick);else setCount(value);
    };
    requestAnimationFrame(tick);
  }, [isActive, value]);
  return <span className="font-default tabular-nums">
      {count}
      <span style={{
      color: "var(--secondary)"
    }}>{suffix}</span>
    </span>;
}
export default function RevenueMetrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="3758" id="guwahatirevenuecasestudy" className="bg-background text-foreground relative overflow-hidden py-32 md:py-40 lg:py-48">
      {}
      <div className="pointer-events-none absolute top-8 left-8 opacity-20">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <line x1="20" y1="0" x2="20" y2="40" stroke="currentColor" strokeWidth="0.5" />
          <line x1="0" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="20" cy="20" r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
        </svg>
      </div>
      <div className="pointer-events-none absolute top-8 right-8 opacity-20">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <line x1="20" y1="0" x2="20" y2="40" stroke="currentColor" strokeWidth="0.5" />
          <line x1="0" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="20" cy="20" r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
        </svg>
      </div>
      <div className="pointer-events-none absolute bottom-8 left-8 opacity-20">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <line x1="20" y1="0" x2="20" y2="40" stroke="currentColor" strokeWidth="0.5" />
          <line x1="0" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="20" cy="20" r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
        </svg>
      </div>
      <div className="pointer-events-none absolute right-8 bottom-8 opacity-20">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <line x1="20" y1="0" x2="20" y2="40" stroke="currentColor" strokeWidth="0.5" />
          <line x1="0" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="20" cy="20" r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
        </svg>
      </div>

      <div className="mx-auto w-full max-w-7xl px-6 lg:px-12">
        {}
        <div className="font-default text-xs tracking-widest uppercase" style={{
        color: "var(--secondary)"
      }}>
          [§02 / PERFORMANCE METRICS]
        </div>

        {}
        <div className="mt-8 grid grid-cols-1 gap-8 md:mt-12 md:grid-cols-12 md:gap-6">
          <h2 className="font-default text-foreground col-span-1 text-4xl leading-[0.95] font-bold tracking-tight md:col-span-8 md:text-6xl lg:text-7xl">
            DATA-DRIVEN
            <br />
            REVENUE GROWTH
          </h2>
          <div className="text-muted-foreground col-span-1 self-end font-default text-sm leading-relaxed md:col-span-3 md:col-start-10">
            Proven results from our revenue management consulting engagements. These figures represent 
            actual performance improvements achieved through our proprietary OTA and direct booking strategies.
          </div>
        </div>

        {}
        <div className="bg-border mt-16 h-px w-full md:mt-20" />

        {}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {metrics.map((metric, i) => <div data-index={i} key={metric.label} className="border-border relative flex flex-col gap-6 px-2 py-12 md:py-16 lg:px-8 lg:py-20 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:nth-child(-n+2)]:border-b lg:[&:nth-child(-n+2)]:border-b-0 md:[&:nth-child(odd)]:border-r lg:[&:not(:last-child)]:border-r">
              {}
              <div className="text-muted-foreground font-default text-[10px] tracking-widest">
                {metric.index}
              </div>

              {}
              <div className="text-foreground text-6xl leading-none font-bold tracking-tight md:text-7xl lg:text-[6.5rem]">
                <AnimatedCounter value={metric.value} suffix={metric.suffix} isActive={isVisible} />
              </div>

              {}
              <div className="text-muted-foreground font-default text-[11px] tracking-widest uppercase">
                {metric.label}
              </div>

              {}
              <div className="mt-2">
                <svg viewBox="0 0 120 36" className="h-8 w-full" fill="none" preserveAspectRatio="none">
                  <path d={metric.sparkline} stroke="var(--secondary)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{
                strokeDasharray: 300,
                strokeDashoffset: isVisible ? 0 : 300,
                transition: "stroke-dashoffset 1.6s ease-out 0.3s"
              }} />
                  <path d={`${metric.sparkline} L120,36 L0,36 Z`} fill="var(--secondary)" opacity={isVisible ? 0.08 : 0} style={{
                transition: "opacity 1.2s ease-out 0.8s"
              }} />
                </svg>
              </div>
            </div>)}
        </div>

        {}
        <div className="bg-border h-px w-full" />
      </div>

      {}
      <div className="border-border mt-16 overflow-hidden border-y py-4 md:mt-20">
        <div className="flex animate-[marquee_40s_linear_infinite] whitespace-nowrap">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <div data-index={i} key={i} className="text-muted-foreground flex items-center font-default text-xs tracking-widest">
              <span className="px-8">{item}</span>
              <span style={{
            color: "var(--secondary)"
          }}>+</span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>;
}